/* eslint-disable */
import React from 'react';
import {
  Grid,
  RACModal,
  RACButton,
  Typography,
  makeStyles,
} from '@rentacenter/racstrap';
import { ReactComponent as WarningIcon } from '../../assets/images/warningIcon.svg';

export interface FundingInProgressWarningModalProps {
  isOpen: boolean;
  vendorName: string;
  productDescription: string;
  approvedAmount: string;
  onClose: () => void;
  onOk?: () => void;
  checkoutFailed?: boolean;
  spendLimitId?: string;
}

const useStyles = makeStyles(() => ({
  dialogContent: {
    padding: '1.5rem',
  },
  dialogActions: {
    padding: '0.75rem 1.5rem',
    justifyContent: 'center',
  },
  dialogRoot: {
    '& .MuiDialog-paperWidthSm': {
      maxWidth: '500px',
      borderRadius: '16px',
    },
  },
  dialogTitle: {
    padding: '1rem 1.5rem 0',
  },
  containerStyle: {
    textAlign: 'center',
    padding: '0.5rem',
  },
  warningIcon: {
    width: '48px',
    height: '48px',
    marginBottom: '0.75rem',
  },
  messageText: {
    fontSize: '0.9rem',
    fontFamily: 'OpenSans-regular',
    color: '#333333',
    lineHeight: 1.5,
    textAlign: 'center',
    padding: '0 0.5rem',
  },
  boldText: {
    fontFamily: 'OpenSans-semibold',
    fontWeight: 700,
  },
  buttonContainer: {
    display: 'flex',
    justifyContent: 'center',
    padding: '1rem 0 0.5rem',
  },
}));

/**
 * Formats a numeric string as US currency (e.g., "395.64" → "$395.64").
 */
function formatCurrency(value: string): string {
  const num = parseFloat(value);
  if (isNaN(num)) return `$${value}`;
  return num.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
  });
}

/**
 * FundingInProgressWarningModal — alert modal shown when coworker clicks any button
 * other than "Continue Checkout" after RAMP generate funding has succeeded.
 *
 * Message per Jira: "You need to finish checking out and buy the <description> with
 * <vendor name> or you will lose the approved <$$$>. Please click the blue Continue
 * Checkout button on the bottom right corner of the screen."
 */
export const FundingInProgressWarningModal: React.FC<
  FundingInProgressWarningModalProps
> = ({ isOpen, vendorName, productDescription, approvedAmount, onClose, onOk, checkoutFailed, spendLimitId }) => {
  const classes = useStyles();

  if (!isOpen) return null;

  return (
    <RACModal
      isOpen={isOpen}
      classes={{
        dialogContent: classes.dialogContent,
        dialogActions: classes.dialogActions,
        dialog: classes.dialogRoot,
        dialogTitle: classes.dialogTitle,
      }}
      title=""
      maxWidth="sm"
      content={
        <Grid container classes={{ root: classes.containerStyle }}>
          <Grid item xs={12}>
            <WarningIcon className={classes.warningIcon} />
          </Grid>
          <Grid item xs={12}>
            {checkoutFailed ? (
              <>
                <Typography className={classes.messageText} style={{ fontWeight: 700, fontSize: '1rem', marginBottom: '0.75rem', color: '#C62828' }}>
                  Checkout Could Not Be Verified
                </Typography>
                <Typography className={classes.messageText}>
                  We are unable to verify the payment completion at this time. If the purchase was completed successfully, it will appear on the Dashboard once authorized.
                </Typography>
                <Typography className={classes.messageText} style={{ marginTop: '0.75rem' }}>
                  You can check the dashboard's pending actions under the Local Sourcing tab to complete the agreement after confirmation is obtained.
                </Typography>
                <Typography className={classes.messageText} style={{ marginTop: '0.75rem' }}>
                  Otherwise, if you need assistance, please contact support and provide the following reference:
                </Typography>
                <Typography className={classes.messageText} style={{ marginTop: '0.5rem', backgroundColor: '#F5F5F5', padding: '0.5rem 1rem', borderRadius: '4px', wordBreak: 'break-all' }}>
                  <span className={classes.boldText}>Reference ID: {spendLimitId || 'N/A'}</span>
                </Typography>
              </>
            ) : (
              <>
                <Typography className={classes.messageText}>
                  You need to finish checking out and buy the{' '}
                  <span className={classes.boldText}>
                    {productDescription || 'item'}
                  </span>{' '}
                  with{' '}
                  <span className={classes.boldText}>
                    {vendorName || 'the vendor'}
                  </span>{' '}
                  or you will lose the approved amount{' '}
                  <span className={classes.boldText}>
                    {formatCurrency(approvedAmount)}
                  </span>
                  .
                </Typography>
                <Typography
                  className={classes.messageText}
                  style={{ marginTop: '1rem' }}
                >
                  Please click the blue{' '}
                  <span className={classes.boldText}>Continue Checkout</span> button
                  on the bottom right corner of the screen.
                </Typography>
              </>
            )}
          </Grid>
        </Grid>
      }
      onClose={onClose}
      buttons={
        <div className={classes.buttonContainer}>
          <RACButton
            // eslint-disable-next-line
            // @ts-ignore
            variant="contained"
            color="primary"
            onClick={onOk || onClose}
          >
            {'OK'}
          </RACButton>
        </div>
      }
    />
  );
};

export default FundingInProgressWarningModal;
